import { EventEmitter, Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CONFIG_CONTEXT } from '../enum/config-context.enum';
import { KingfisherService } from './kingfisher/kingfisher.service';
import { CreateCustomer } from './kingfisher/create-customer';
import { Customer } from './kingfisher/customer';
import { CurrentService } from './current.service';
import { environment } from 'src/environments/environment';
import { logError } from './helpers/sentry.helper';
import { CiamService, CiamTokens } from './ciam/ciam.service';
import { isCustomerProfileIncomplete } from './ciam/ciam.models';
import { MODAL, ModalService } from './modal.service';

export const CUSTOMER_EVENT_NEW_PROJECT_REQUESTED = 'NEW_PROJECT_REQUESTED';
export const CUSTOMER_EVENT_LOGGED_OUT_ELSEWHERE = 'LOGGED_OUT_ELSEWHERE';

@Injectable({
  providedIn: 'root',
})
export class CustomerService {
  public context: string = CONFIG_CONTEXT.KITCHEN;
  public token: string;
  public ciamAccessToken: string;
  public userDatas: Customer;

  public legacyToken: string;
  public legacyCustomerId: string;

  public events: EventEmitter<any>;

  constructor(
    private readonly http: HttpClient,
    private readonly kfService: KingfisherService,
    private readonly ciamService: CiamService,
    private readonly modalService: ModalService,
    private readonly injector: Injector
  ) {
    this.events = new EventEmitter();

    // Le client s'est déconnecté dans un autre onglet : cet onglet oublie aussi sa session.
    this.ciamService.loggedOutElsewhere.subscribe(() => {
      this.clearSession();
      this.events.emit({ type: CUSTOMER_EVENT_LOGGED_OUT_ELSEWHERE });
    });
  }

  get isLogged(): boolean {
    return (this.token != null || this.ciamAccessToken != null) && this.userDatas != null;
  }

  /**
   * Affiché dans l'entête : une fiche encore incomplète ne doit pas y écrire « undefined ».
   * Le prénom est capitalisé, le nom en majuscules, et les parties manquantes s'effacent.
   */
  get fullName(): string {
    if (!this.isLogged) {
      return '';
    }

    const firstname = (this.userDatas?.firstname ?? '').trim();
    const lastname = (this.userDatas?.lastname ?? '').trim();
    const capitalized = firstname ? `${firstname.charAt(0).toUpperCase()}${firstname.slice(1)}` : '';

    return [capitalized, lastname.toUpperCase()].filter(Boolean).join(' ');
  }

  logout(): void {
    if (!environment.vendor) {
      this.ciamService.logout().catch(logError);
    }

    this.clearSession();
  }

  /** Oublie tout ce que le site garde du client connecté : jetons CIAM et HomeByMe, profil. */
  private clearSession(): void {
    this.token = null;
    this.ciamAccessToken = null;
    this.userDatas = null;
    this.legacyToken = null;
    this.legacyCustomerId = null;
  }

  async restoreCiamSession(): Promise<boolean> {
    if (environment.vendor) {
      return false;
    }

    const tokens = await this.ciamService.restoreOrRefreshTokens();
    if (!tokens?.accessToken) {
      return false;
    }

    try {
      await this.applyCiamTokens(tokens);
      return true;
    } catch (err) {
      logError(err);
      this.ciamService.clearTokens();
      this.clearSession();
      return false;
    }
  }

  /**
   * Finalise le retour d'AuthApp. Les attributs qu'AuthApp ne collecte pas sont demandés au
   * client par la popup de complétion, armée par `applyCiamTokens` : plus rien n'a donc à
   * traverser la redirection.
   */
  async initializeCiamSession(): Promise<void> {
    const tokens = await this.ciamService.exchangeSessionForTokens();
    await this.applyCiamTokens(tokens);

    this.events.emit({ type: 'login', provider: 'ciam' });
  }

  /**
   * Renvoie l'access token du client connecté via CIAM.
   * Le build Vendor n'authentifie pas de client avec ses propres credentials.
   */
  async getValidKingfisherToken(): Promise<string> {
    if (environment.vendor) {
      return undefined;
    }

    const tokens = await this.ciamService.restoreOrRefreshTokens();
    if (!tokens?.accessToken) {
      return undefined;
    }

    this.setCiamTokens(tokens);
    return tokens.accessToken;
  }

  lookForLegacyCustomerId(sapId: string): Promise<string> {
    return new Promise((resolve, reject) => {
      console.time('lookForLegacyCustomerId');

      let _localDbFile: string = '/assets/utils/legacyID_to_sapID/s2l_' + sapId.slice(-2) + '.json';

      this.http.get(_localDbFile).subscribe(
        (localBase: any) => {
          let _unpreffixedSapID = sapId.replace('SQ_', '');
          let _found = localBase.find((row: any) => {
            return row[0] == _unpreffixedSapID;
          });

          if (_found) {
            console.timeEnd('lookForLegacyCustomerId');
            this.legacyCustomerId = _found[1];
            resolve(_found[1]);
          } else {
            resolve(null);
          }
        },
        (error: any) => {
          resolve(null);
        }
      );
    });
  }

  /**
   * @param returnUrl URL à rejouer après AuthApp. Indispensable depuis un guard : la navigation
   * n'étant pas encore commitée, l'URL courante du navigateur est encore la précédente.
   */
  login(returnUrl?: string): void {
    this.ciamService.redirectToSignin(returnUrl);
  }

  /**
   * Ouvre AuthApp dans un nouvel onglet, à réserver aux écrans dont l'état n'est pas rejouable
   * (une configuration 3D en cours) : une redirection pleine page le perdrait.
   * Retourne `false` si le navigateur a bloqué l'ouverture.
   */
  loginInNewTab(): boolean {
    return this.ciamService.openSigninInNewTab();
  }

  /**
   * Reprend la session après une reconnexion faite dans un autre onglet. Le cookie AuthApp est
   * partagé entre les onglets, pas le sessionStorage : ré-échanger la session est le seul moyen
   * pour cet onglet-ci de retrouver un jeton exploitable.
   */
  async resumeCiamSession(): Promise<boolean> {
    if (environment.vendor) {
      return false;
    }

    try {
      const tokens = await this.ciamService.exchangeSessionForTokens();
      await this.applyCiamTokens(tokens);
      return true;
    } catch (err) {
      // Le client n'a pas (encore) terminé sa reconnexion : l'appelant lui reproposera.
      logError(err);
      return false;
    }
  }

  /** URL que le client visait en partant vers AuthApp, à rejouer une seule fois au retour. */
  consumeLoginReturnUrl(): string | null {
    return this.ciamService.consumeReturnUrl();
  }

  forgottenPassword(): void {
    this.ciamService.redirectToForgotPassword();
  }

  async register(formData: CreateCustomer): Promise<void> {
    try {
      if (environment.vendor) {
        // Insertion du CurrentService en lazy loading à cause d'une référence cyclique entre CurrentService et CustomerService
        const current = this.injector.get(CurrentService);
        const token = await current.vendor.accessToken();
        this.userDatas = await this.kfService.registerByColleague(formData, token);
      } else {
        // Le site client n'assemble plus de fiche avant AuthApp : `formData` n'est pas transmis,
        // la popup de complétion collecte les mêmes attributs au retour, client authentifié.
        this.ciamService.redirectToRegistration();
        return new Promise<void>(() => {});
      }
    } catch (err) {
      logError(err);
      throw err;
    }
  }

  async loadUserByKfId(kfId: string): Promise<void> {
    try {
      if (environment.vendor) {
        const current = this.injector.get(CurrentService);
        const token = await current.vendor.accessToken();
        this.userDatas = await this.kfService.getCustomerInfoByColleague(token, kfId);
      }
    } catch (err) {
      logError(err);
      throw err;
    }
  }

  /** Envoie le client s'inscrire sur AuthApp, sans pré-formulaire. */
  signup(returnUrl?: string): void {
    this.ciamService.redirectToRegistration(returnUrl);
  }

  /**
   * Complète la fiche GCI du client connecté. Le client est déjà authentifié : il n'y a rien
   * à créer côté CIAM, seulement à patcher les attributs que la gateway ne connaît pas.
   */
  async completeCurrentCustomerProfile(formData: CreateCustomer): Promise<void> {
    const accessToken = await this.getValidKingfisherToken();
    this.userDatas = await this.ciamService.updateCurrentCustomer(accessToken, formData);
  }

  private async applyCiamTokens(tokens: CiamTokens): Promise<void> {
    this.setCiamTokens(tokens);
    this.userDatas = await this.ciamService.getCurrentCustomer(tokens.accessToken);
    this.requireProfileCompletion();
  }

  /**
   * Armé à chaque obtention de session CIAM — retour d'AuthApp, restauration au rechargement
   * de page, reprise depuis un autre onglet — pour qu'un F5 ne permette pas d'échapper à la
   * saisie. La modale est bloquante : elle ne se ferme qu'une fois le PATCH GCI réussi.
   */
  private requireProfileCompletion(): void {
    if (environment.vendor || !isCustomerProfileIncomplete(this.userDatas)) {
      return;
    }

    this.modalService.display(MODAL.CIAM_PROFILE_COMPLETION, { customer: this.userDatas });
  }

  private setCiamTokens(tokens: CiamTokens): void {
    this.ciamAccessToken = tokens.accessToken;
  }
}
